<template>
  <div class="view" v-if="canEdit" v-cloak>
    <EditCreateTitleBox :label="title" />
    <div v-if="isLoading" class="loader-container">
      <Loader :loading="isLoading" size="sm"></Loader>
    </div>
    <div class="quantity-warning-add-edit-container" v-else>
      <!-- Left Box -->
      <StepsPanelWrapper
        ref="stepsWrapper"
        :steps="steps"
        :current-step.sync="currentStep"
        :step-props-fn="getStepProps"
        @step-input="onStepInput"
        @validation-error="handleValidationError"
      />

      <!-- Right Box -->
      <QuantityWarningRightPanel :value="formData" :saving="saving" @save="save" @back="gotoList" />
    </div>
  </div>
  <div v-else>
    <Forbidden />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import settingsStep from "./partials/settings.vue";
import productsStep from "./partials/products.vue";
import QuantityWarningRightPanel from "./partials/QuantityWarningRightPanel.vue";
import { canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import Forbidden from "@/pages/errors/Forbidden";
import EditCreateTitleBox from "@/components/Common/EditCreateTitleBox.vue";
import StepsPanelWrapper from "@/components/Common/StepsPanelWrapper.vue";
import { successMessage, errorMessage, handlePromiseException } from "@/helpers/common";
import Loader from "@/components/Loader";

export default {
  name: "QuantityWarningsAddOrEdit",
  data() {
    return {
      title: this.$t("scale.quantity_warnings.edit"),
      isLoading: false,
      formData: {
        uuid: null,
        legal_entity_uuid: null,
        legal_entity_name: "",
        year: new Date().getFullYear(),
        include_weighing_on_scale: 1,
        weighing_type_ids: [],
        include_stock_movements: 0,
        stock_movement_type_ids: [],
        products: [],
      },
      steps: [
        {
          number: 1,
          component: settingsStep,
          label: this.$t("common.settings"),
          ref: "QuantityWarningSettings",
        },
        {
          number: 2,
          component: productsStep,
          label: this.$t("common.product"),
          ref: "QuantityWarningProducts",
        },
      ],
      currentStep: 1,
      saving: false,
    };
  },
  computed: {
    ...mapGetters({}),
    config() {
      return {
        permission: permissions.QUANTITY_WARNINGS,
        permission_type: permissionTypes.access,
      };
    },
    canEdit() {
      return canEdit(this);
    },
  },
  components: {
    settingsStep,
    productsStep,
    QuantityWarningRightPanel,
    Forbidden,
    EditCreateTitleBox,
    StepsPanelWrapper,
    Loader,
  },
  methods: {
    ...mapActions({
      createQuantityWarning: "scaleQuantityWarnings/createQuantityWarning",
      updateQuantityWarning: "scaleQuantityWarnings/updateQuantityWarning",
      getQuantityWarningByUuid: "scaleQuantityWarnings/getQuantityWarningByUuid",
    }),
    handleAddSave(dataToSave) {
      this.createQuantityWarning(dataToSave)
        .then((response) => {
          successMessage(this, response.message);
          this.$router.push({ name: "quantity-warnings" });
        })
        .catch((e) => {
          handlePromiseException(this, e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    handleEditSave(dataToSave) {
      this.updateQuantityWarning(dataToSave)
        .then((response) => {
          successMessage(this, response.message);
          this.$router.push({ name: "quantity-warnings" });
        })
        .catch((e) => {
          handlePromiseException(this, e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    async fetchData(uuid) {
      try {
        const response = await this.getQuantityWarningByUuid({ uuid });
        if (response && response.data) {
          this.formData = { ...this.formData, ...response.data };

          for (const step of this.steps) {
            const stepComponent = this.$refs?.[step.ref];
            if (stepComponent && typeof stepComponent.updateData === "function") {
              if (step.number === 1) {
                stepComponent.updateData(response.data);
              } else {
                setTimeout(() => {
                  stepComponent.updateData(response.data);
                }, step.number * 10);
              }
            }
          }
        }
      } finally {
        this.isLoading = false;
      }
    },
    getStepProps() {
      return {
        formData: this.formData,
      };
    },
    onStepInput(step, payload) {
      if (!step) return;
      this.formData = { ...this.formData, ...(payload || {}) };
    },
    handleValidationError(error) {
      errorMessage(this, error.message || this.$t("fill_out_error"));
    },
    save() {
      this.validateStepsAndSave();
    },
    async validateStepsAndSave() {
      const wrapper = this.$refs?.stepsWrapper;
      if (wrapper && typeof wrapper.validateSteps === "function") {
        const errors = await Promise.resolve(wrapper.validateSteps());
        if (errors && errors.length) {
          errors.forEach((msg) => errorMessage(this, msg));
          return;
        }
      }

      this.saving = true;
      const dataToSave = { ...this.formData };

      if (dataToSave.uuid) {
        this.handleEditSave(dataToSave);
      } else {
        this.handleAddSave(dataToSave);
      }
    },
    gotoList() {
      this.$router.push({ name: "quantity-warnings" });
    },
  },
  mounted() {
    if (this.$route.params.uuid && this.$route.params.uuid !== "add") {
      this.formData.uuid = this.$route.params.uuid;
      this.title = this.$t("scale.quantity_warnings.edit");
      this.isLoading = true;
      setTimeout(() => {
        this.fetchData(this.$route.params.uuid);
      }, 300);
    } else {
      this.formData.uuid = null;
      this.title = this.$t("scale.quantity_warnings.create_new");
    }
  },
};
</script>

<style scoped>
.view {
  height: calc(100% - 70px);
}

.quantity-warning-add-edit-container {
  display: flex;
  height: 100%;
  width: 100%;
}
</style>

<style>
.quantity-warning-add-edit-container .dx-selectbox {
  margin-top: 8px !important;
}

.quantity-warning-add-edit-container .dx-textbox {
  margin-top: 8px !important;
}
</style>
